import {
    AdminPanelSettingsOutlined,
    KeyOutlined,
    PeopleOutline,
    ShieldOutlined,
} from '@mui/icons-material';
import {Box, Grid, Tab, Tabs} from '@mui/material';
import {FC, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {useSearchParams} from 'react-router-dom';
import {useGetPermissionCatalogQuery, useGetRolesQuery} from 'src/api';
import {PageHeader} from 'src/components/common/PageHeader';
import {PermissionsSection} from 'src/components/iam/PermissionsSection';
import {RoleFormModal} from 'src/components/iam/RoleFormModal';
import {RolesSection} from 'src/components/iam/RolesSection';
import {UsersSection} from 'src/components/iam/UsersSection';
import {KpiCard} from 'src/pages/EnergyConsumptionReport/components/KpiCard';
import {useCan} from 'src/permissions/hooks';
import {pageSx, sectionHintSx, tabsSx} from './AccessControl.sx';
import {Typography} from '@mui/material';
import {FormattedMessage} from 'react-intl';
import {useBusinessId} from 'src/app/hooks';

type Section = 'users' | 'roles' | 'permissions';

const SECTIONS: Section[] = ['users', 'roles', 'permissions'];

/**
 * Access control, in the order the questions are actually asked.
 *
 * People first: nearly every visit starts with a person and ends with "what can they do". Roles
 * second, because that is the answer to most of those questions. The catalog last, as reference.
 *
 * The section lives in the URL rather than in state, so a link to a tab is a link to a tab — this
 * screen gets shared between people working out why somebody was refused something.
 */
export const AccessControl: FC = () => {
    const intl = useIntl();
    const [params, setParams] = useSearchParams();
    const canManageRoles = useCan('accessControl.manageRoles');
    const [creatingRole, setCreatingRole] = useState(false);
    // Roles belong to a business, so the list follows the one being worked in.
    const businessId = useBusinessId();

    const {data: roles} = useGetRolesQuery(businessId);
    const {data: catalog} = useGetPermissionCatalogQuery();

    const requested = params.get('section') as Section | null;
    const section: Section = requested && SECTIONS.includes(requested) ? requested : 'users';

    const stats = useMemo(() => {
        const all = roles ?? [];
        return {
            inUse: all.filter((role) => (role.holders ?? 0) > 0).length,
            defined: all.length,
            people: all.reduce((total, role) => total + (role.holders ?? 0), 0),
            permissions: catalog?.length ?? 0,
        };
    }, [roles, catalog]);

    // Every list screen in this application puts its primary action in the page header, in the brand
    // colour, and nowhere else. A button beside the search box was what made this area look bolted on.
    const showCreateRole = section === 'roles' && canManageRoles;

    return (
        <Box sx={pageSx}>
            <PageHeader
                title={intl.formatMessage({id: 'accessControl', defaultMessage: 'Access'})}
                onlyTitle={!showCreateRole}
                buttonText="accessControlCreateRole"
                onClick={() => setCreatingRole(true)}
                icon={<AdminPanelSettingsOutlined/>}
            />

            <Typography sx={sectionHintSx}>
                <FormattedMessage
                    id="accessControlLegend"
                    defaultMessage="Roles decide what people can do. Everyone holds exactly one role. Anything a role does not list is refused automatically — you never have to refuse it explicitly."
                />
            </Typography>

            <Grid container spacing={2} sx={{mb: 1}}>
                <Grid item xs={6} sm={4} md={2.4}>
                    <KpiCard
                        label={intl.formatMessage({
                            id: 'accessControlRolesInUse',
                            defaultMessage: 'Roles in use',
                        })}
                        value={String(stats.inUse)}
                        unit={intl.formatMessage(
                            {id: 'accessControlOfDefined', defaultMessage: 'of {total} defined'},
                            {total: stats.defined}
                        )}
                        icon={<ShieldOutlined fontSize="small"/>}
                    />
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                    <KpiCard
                        label={intl.formatMessage({
                            id: 'accessControlPeopleWithAccess',
                            defaultMessage: 'People with access',
                        })}
                        value={String(stats.people)}
                        unit={intl.formatMessage({
                            id: 'accessControlHoldARole',
                            defaultMessage: 'hold a role',
                        })}
                        icon={<PeopleOutline fontSize="small"/>}
                    />
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                    <KpiCard
                        label={intl.formatMessage({
                            id: 'accessControlTabPermissions',
                            defaultMessage: 'Permissions',
                        })}
                        value={String(stats.permissions)}
                        unit={intl.formatMessage({
                            id: 'accessControlInTheCatalog',
                            defaultMessage: 'in the catalog',
                        })}
                        icon={<KeyOutlined fontSize="small"/>}
                    />
                </Grid>
            </Grid>

            <Box sx={{borderBottom: 1, borderColor: 'divider', mt: 3}}>
                <Tabs
                    sx={tabsSx}
                    value={section}
                    onChange={(_event, value: Section) => setParams({section: value})}
                >
                    <Tab
                        value="users"
                        label={intl.formatMessage({
                            id: 'accessControlTabUsers',
                            defaultMessage: 'People',
                        })}
                    />
                    <Tab
                        value="roles"
                        label={intl.formatMessage({
                            id: 'accessControlTabRoles',
                            defaultMessage: 'Roles',
                        })}
                    />
                    <Tab
                        value="permissions"
                        label={intl.formatMessage({
                            id: 'accessControlTabPermissions',
                            defaultMessage: 'Permissions',
                        })}
                    />
                </Tabs>
            </Box>

            <Box sx={{mt: 2.5}}>
                {section === 'users' && <UsersSection/>}
                {section === 'roles' && <RolesSection/>}
                {section === 'permissions' && <PermissionsSection/>}
            </Box>

            {creatingRole && <RoleFormModal onClose={() => setCreatingRole(false)}/>}
        </Box>
    );
};
